import { useState, useMemo, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { ENDPOINTS } from '../../utils/constants';
import './ProductSummaryTable.css';

const fmtValue = (v) =>
  v > 0
    ? `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '—';

/**
 * Aggregates all order line items into a product-level summary.
 * Each product row is clickable to expand and show per-variant unit counts.
 */
/**
 * Open the HTML label for a variant in a new window sized for a 4×6 label.
 * @param {number} variantId
 * @param {string} [customerName]  When provided, UPC is replaced with customer name
 */
const openLabel = (variantId, customerName = '') => {
  if (!variantId) return;
  const qs = customerName
    ? `?customer=${encodeURIComponent(customerName)}`
    : '';
  window.open(
    `/print-label?labelHtml=/api${ENDPOINTS.PRODUCTS.VARIANT_LABEL_HTML(variantId)}${qs}`,
    '_blank',
    'width=480,height=720',
  );
};

const ProductSummaryTable = ({ orders = [], loading = false }) => {
  const [expandedProducts, setExpandedProducts] = useState(new Set());
  // Track which variant's print menu is open: key = `${productName}__${variantName}`
  const [openPrintMenu, setOpenPrintMenu] = useState(null);

  const togglePrintMenu = (key, e) => {
    e.stopPropagation();
    setOpenPrintMenu((prev) => (prev === key ? null : key));
  };

  // Close menu when clicking outside
  const handleBodyClick = () => setOpenPrintMenu(null);
  useEffect(() => {
    document.addEventListener('click', handleBodyClick);
    return () => document.removeEventListener('click', handleBodyClick);
  }, []);

  // Build product → variants map from all order items
  const { products, grandTotal, grandValue } = useMemo(() => {
    // productName → { totalQty, totalValue, variants: { variantName → { qty, value, variantId, upcCode, customers: { name → qty } } } }
    const map = {};

    orders.forEach((order) => {
      if (order.status === 'cancelled') return; // exclude cancelled
      const customerName = order.company_name || '';
      (order.items || []).forEach((item) => {
        const pName     = item.product_variant?.product_name || 'Unknown';
        const vName     = item.product_variant?.variant_name || 'Unknown';
        const variantId = item.product_variant?.id || null;
        const upcCode   = item.product_variant?.upc_code || '';
        const qty       = Number(item.quantity) || 0;
        const value     = Number(item.subtotal_dollars) || 0;

        if (!map[pName]) {
          map[pName] = { totalQty: 0, totalValue: 0, variants: {} };
        }
        map[pName].totalQty   += qty;
        map[pName].totalValue += value;

        if (!map[pName].variants[vName]) {
          map[pName].variants[vName] = { qty: 0, value: 0, variantId, upcCode, customers: {} };
        }
        map[pName].variants[vName].qty   += qty;
        map[pName].variants[vName].value += value;
        // Keep variantId and upcCode if we see one
        if (variantId && !map[pName].variants[vName].variantId) {
          map[pName].variants[vName].variantId = variantId;
        }
        if (upcCode && !map[pName].variants[vName].upcCode) {
          map[pName].variants[vName].upcCode = upcCode;
        }
        // Track per-customer quantities for "print with customer name"
        if (customerName) {
          const prev = map[pName].variants[vName].customers[customerName] || 0;
          map[pName].variants[vName].customers[customerName] = prev + qty;
        }
      });
    });

    // Sort products by total qty desc
    const products = Object.entries(map)
      .map(([name, data]) => ({
        name,
        totalQty:   data.totalQty,
        totalValue: data.totalValue,
        variants: Object.entries(data.variants)
          .map(([vName, vData]) => ({
            name: vName,
            qty: vData.qty,
            value: vData.value,
            variantId: vData.variantId,
            upcCode: vData.upcCode,
            // Sort customers by name for consistent menu order
            customers: Object.entries(vData.customers)
              .map(([cName, cQty]) => ({ name: cName, qty: cQty }))
              .sort((a, b) => a.name.localeCompare(b.name)),
          }))
          .sort((a, b) => b.qty - a.qty),
      }))
      .sort((a, b) => b.totalQty - a.totalQty);

    const grandTotal = products.reduce((s, p) => s + p.totalQty, 0);
    const grandValue = products.reduce((s, p) => s + p.totalValue, 0);

    return { products, grandTotal, grandValue };
  }, [orders]);

  const toggleProduct = (name) => {
    setExpandedProducts((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="pst__empty">
        <p className="pst__empty-text">Loading…</p>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="pst__empty">
        <p className="pst__empty-label">No Data</p>
        <p className="pst__empty-sub">No order items match the current filters.</p>
      </div>
    );
  }

  return (
    <div className="pst-wrap">

      {/* ── Stats strip ──────────────────────────────────────────── */}
      <div className="pst-stats">
        <div className="pst-stat">
          <span className="pst-stat__value">{products.length}</span>
          <span className="pst-stat__label">{products.length === 1 ? 'Product' : 'Products'}</span>
        </div>
        <div className="pst-stat pst-stat--divider">
          <span className="pst-stat__value">{grandTotal.toLocaleString()}</span>
          <span className="pst-stat__label">Total Units</span>
        </div>
        <div className="pst-stat pst-stat--divider pst-stat--highlight">
          <span className="pst-stat__value">{fmtValue(grandValue)}</span>
          <span className="pst-stat__label">Total Value</span>
        </div>
        <p className="pst-stats__note">Excludes cancelled orders</p>
      </div>

      {/* ── Table ────────────────────────────────────────────────── */}
      <div className="pst-scroll">
        <table className="pst">
          <thead className="pst__thead">
            <tr>
              <th className="pst__th pst__th--product">Product</th>
              <th className="pst__th pst__th--num">Units</th>
              <th className="pst__th pst__th--num">Value</th>
              <th className="pst__th pst__th--pct pst__th--share">Share</th>
              <th className="pst__th pst__th--print" />
            </tr>
          </thead>
          <tbody>
            {products.map((product) => {
              const isExpanded = expandedProducts.has(product.name);
              const pct = grandTotal > 0 ? ((product.totalQty / grandTotal) * 100).toFixed(1) : '0.0';

              return (
                <Fragment key={product.name}>
                  {/* ── Product row ── */}
                  <tr
                    className={`pst__row pst__row--product${isExpanded ? ' pst__row--expanded' : ''}`}
                    onClick={() => toggleProduct(product.name)}
                    role="button"
                    tabIndex={0}
                    aria-expanded={isExpanded}
                    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && toggleProduct(product.name)}
                  >
                    <td className="pst__td pst__td--product">
                      <div className="pst__cell-inner">
                        <span className={`pst__chevron${isExpanded ? ' pst__chevron--open' : ''}`}>›</span>
                        <span className="pst__product-name">{product.name}</span>
                      </div>
                    </td>
                    <td className="pst__td pst__td--num pst__td--bold">{product.totalQty.toLocaleString()}</td>
                    <td className="pst__td pst__td--num">{fmtValue(product.totalValue)}</td>
                    <td className="pst__td pst__td--pct pst__td--share">
                      <span className="pst__bar-wrap">
                        <span className="pst__bar" style={{ width: `${pct}%` }} />
                      </span>
                      <span className="pst__pct-label">{pct}%</span>
                    </td>
                    <td className="pst__td pst__td--print" />
                  </tr>

                  {/* ── Variant rows (expanded) ── */}
                  {isExpanded && product.variants.map((variant) => {
                    const vPct = grandTotal > 0 ? ((variant.qty / grandTotal) * 100).toFixed(1) : '0.0';
                    return (
                      <tr key={`${product.name}__${variant.name}`} className="pst__row pst__row--variant">
                        <td className="pst__td pst__td--variant">{variant.name}</td>
                        <td className="pst__td pst__td--num">{variant.qty.toLocaleString()}</td>
                        <td className="pst__td pst__td--num">{fmtValue(variant.value)}</td>
                        <td className="pst__td pst__td--pct pst__td--share">
                          <span className="pst__bar-wrap">
                            <span className="pst__bar pst__bar--variant" style={{ width: `${vPct}%` }} />
                          </span>
                          <span className="pst__pct-label">{vPct}%</span>
                        </td>
                        <td className="pst__td pst__td--print">
                          {variant.variantId && (() => {
                            const menuKey = `${product.name}__${variant.name}`;
                            const isMenuOpen = openPrintMenu === menuKey;
                            const hasCustomers = variant.customers.length > 0;
                            return (
                              <div className="pst__print-wrap" onClick={(e) => e.stopPropagation()}>
                                {/* Main print button (generic / UPC label) */}
                                <button
                                  type="button"
                                  className="pst__print-btn"
                                  title={`Print generic label for ${variant.name}`}
                                  onClick={() => openLabel(variant.variantId)}
                                >
                                  🖨
                                </button>
                                {/* Chevron to open customer dropdown — only shown if there are customers */}
                                {hasCustomers && (
                                  <button
                                    type="button"
                                    className={`pst__print-chevron${isMenuOpen ? ' pst__print-chevron--open' : ''}`}
                                    title="Print label with customer name"
                                    onClick={(e) => togglePrintMenu(menuKey, e)}
                                  >
                                    ›
                                  </button>
                                )}
                                {/* Customer dropdown */}
                                {isMenuOpen && (
                                  <div className="pst__print-menu">
                                    <div className="pst__print-menu-header">Print with customer name</div>
                                    {variant.customers.map((c) => (
                                      <button
                                        key={c.name}
                                        type="button"
                                        className="pst__print-menu-item"
                                        onClick={() => {
                                          setOpenPrintMenu(null);
                                          openLabel(variant.variantId, c.name);
                                        }}
                                      >
                                        <span className="pst__print-menu-customer">{c.name}</span>
                                        <span className="pst__print-menu-qty">×{c.qty}</span>
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                      </tr>
                    );
                  })}
                </Fragment>
              );
            })}
          </tbody>

          {/* ── Footer totals ── */}
          <tfoot className="pst__tfoot">
            <tr>
              <td className="pst__td pst__td--total-label">Total</td>
              <td className="pst__td pst__td--num pst__td--bold">
                {grandTotal.toLocaleString()}
              </td>
              <td className="pst__td pst__td--num pst__td--bold">
                {fmtValue(grandValue)}
              </td>
              <td className="pst__td pst__td--share" />
              <td className="pst__td pst__td--print" />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

ProductSummaryTable.propTypes = {
  orders: PropTypes.arrayOf(
    PropTypes.shape({
      status: PropTypes.string,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          product_variant: PropTypes.shape({
            id: PropTypes.number,
            product_name: PropTypes.string,
            variant_name: PropTypes.string,
            upc_code: PropTypes.string,
            label_url: PropTypes.string,
          }),
          quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          subtotal_dollars: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        })
      ),
    })
  ),
  loading: PropTypes.bool,
};

export default ProductSummaryTable;
